import React, { useState, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  IconButton,
  Container,
  Fade,
  Grow,
} from "@mui/material";
import MicIcon from "@mui/icons-material/Mic";
import StopIcon from "@mui/icons-material/Stop";
import DeleteIcon from "@mui/icons-material/Delete";
import AudioFileIcon from "@mui/icons-material/AudioFile";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { CircularProgress } from "@mui/material";

const REACT_APP_BACKEND_ROUTE_URL = process.env.REACT_APP_BACKEND_ROUTE_URL;

const VoiceUploadByPatient = () => {
  const [recordings, setRecordings] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);

  // Read QR token from URL
  const urlParams = new URLSearchParams(window.location.search);
  const qrToken = urlParams.get("token");

  const formatDuration = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, {
          type: "audio/webm",
        });
        const timestamp = new Date().toLocaleTimeString();
        setRecordings((prev) => [
          ...prev,
          {
            name: `Recording_${prev.length + 1}_${timestamp}`,
            blob: audioBlob,
            duration: recordingDuration,
            url: URL.createObjectURL(audioBlob),
          },
        ]);
        setRecordingDuration(0);
        stream.getTracks().forEach((track) => track.stop());
        toast.info("Recording saved.");
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingDuration(0);

      timerRef.current = setInterval(() => {
        setRecordingDuration((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      console.error("Microphone access error:", error);
      toast.error(
        "Unable to access microphone. Please allow microphone permissions."
      );
    }
  };

  const stopRecording = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== "inactive"
    ) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(timerRef.current);
    }
  };

  const removeRecording = (index) => {
    setRecordings((prev) => {
      const removed = prev[index];
      if (removed?.url) URL.revokeObjectURL(removed.url);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleUpload = async () => {
    if (!qrToken) {
      toast.error(
        "Invalid or expired QR link. Please scan a new QR code from your doctor."
      );
      return;
    }

    if (recordings.length === 0) {
      toast.warning("Please record at least one voice clip to upload.");
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append("upload_type", "voice");

    recordings.forEach((rec) => {
      const file = new File([rec.blob], `${rec.name}.webm`, {
        type: "audio/webm",
      });
      formData.append("files", file);
    });

    try {
      const cleanAxios = axios.create();

      const response = await cleanAxios.post(
        `${REACT_APP_BACKEND_ROUTE_URL}patient-qr-upload/?token=${encodeURIComponent(
          qrToken
        )}&upload_type=voice`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            "QR-Token": qrToken,
          },
        }
      );

      if (response.data?.status === "Success" || response.status === 200) {
        toast.success(
          response.data?.message || "Voice recordings uploaded successfully!"
        );
        recordings.forEach((rec) => {
          if (rec?.url) URL.revokeObjectURL(rec.url);
        });
        setRecordings([]);
      } else {
        toast.error(
          response.data?.message || "Upload failed. Please try again."
        );
      }
    } catch (error) {
      console.error("Upload error:", error);
      const status = error.response?.status;
      const errorMessage = error.response?.data?.message;

      if (status === 401 || status === 403) {
        toast.error(
          errorMessage ||
            "QR link has expired or is invalid. Please scan a new QR code from your doctor."
        );
      } else {
        toast.error(
          errorMessage ||
            "Error uploading recordings. Please check your connection."
        );
      }
    } finally {
      setIsUploading(false);
    }
  };

  // Show error state if no token is present
  if (!qrToken) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          background: "#132140",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: { xs: 2, sm: 4 },
          px: { xs: 1, sm: 2 },
        }}
      >
        <ToastContainer position="top-center" autoClose={3000} />
        <Container
          maxWidth={false}
          sx={{ width: { xs: "100%", sm: "95%", md: "600px" } }}
        >
          <Card
            sx={{
              p: { xs: 3, sm: 5 },
              borderRadius: 3,
              bgcolor: "#f8fafc",
              textAlign: "center",
              boxShadow:
                "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
            }}
          >
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, color: "#dc2626", mb: 2 }}
            >
              Invalid Link
            </Typography>
            <Typography variant="body1" sx={{ color: "#64748b" }}>
              This upload link is invalid or has expired. Please scan a new QR
              code from your doctor's console.
            </Typography>
          </Card>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "#132140",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        py: { xs: 2, sm: 4 },
        px: { xs: 1, sm: 2 },
      }}
    >
      <ToastContainer position="top-center" autoClose={3000} />
      <Container
        maxWidth={false}
        sx={{
          width: { xs: "100%", sm: "95%", md: "600px" },
        }}
      >
        <Grow in={true} timeout={800}>
          <Card
            sx={{
              p: { xs: 3, sm: 5 },
              borderRadius: 3,
              bgcolor: "#f8fafc",
              boxShadow:
                "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
            }}
          >
            <Box sx={{ textAlign: "center", mb: 4 }}>
              <Typography
                variant="h5"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  color: "#1e293b",
                  fontSize: { xs: "1.5rem", sm: "1.85rem" },
                }}
              >
                Doctors Voice Upload
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "center",
                  bgcolor: "#eef2ff",
                  px: 2.5,
                  py: 0.5,
                  borderRadius: 5,
                  border: "1px solid #d0d7f7",
                  mt: 1,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: "#1e3a8a",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                  }}
                >
                  Secure Session Active
                </Typography>
              </Box>
            </Box>

            {/* Recording Area */}
            <Box
              sx={{
                p: { xs: 4, sm: 6 },
                border: isRecording
                  ? "2px solid #ef4444"
                  : "2px dashed #94a3b8",
                borderRadius: 3,
                textAlign: "center",
                cursor: "pointer",
                bgcolor: isRecording ? "#fef2f2" : "#e2e8f0",
                transition: "all 0.3s ease",
                "&:hover": {
                  bgcolor: isRecording ? "#fee2e2" : "#cbd5e1",
                  borderColor: isRecording ? "#dc2626" : "#64748b",
                },
                "&:active": { transform: "scale(0.99)" },
                mb: 3,
              }}
              onClick={isRecording ? stopRecording : startRecording}
            >
              {isRecording ? (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        borderRadius: "50%",
                        bgcolor: "#ef4444",
                        animation: "pulse 1.5s infinite",
                        "@keyframes pulse": {
                          "0%": { opacity: 1, transform: "scale(1)" },
                          "50%": { opacity: 0.5, transform: "scale(1.2)" },
                          "100%": { opacity: 1, transform: "scale(1)" },
                        },
                      }}
                    />
                    <Typography
                      variant="h4"
                      sx={{ fontWeight: 700, color: "#dc2626" }}
                    >
                      {formatDuration(recordingDuration)}
                    </Typography>
                  </Box>
                  <StopIcon
                    sx={{
                      fontSize: { xs: 50, sm: 60 },
                      color: "#dc2626",
                      mb: 1,
                    }}
                  />
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, color: "#dc2626" }}
                  >
                    Tap To Stop Recording
                  </Typography>
                </>
              ) : (
                <>
                  <MicIcon
                    sx={{
                      fontSize: { xs: 60, sm: 70 },
                      color: "#1e293b",
                      mb: 2,
                    }}
                  />
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, color: "#1e293b", mb: 1 }}
                  >
                    Tap To Start Recording
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#64748b" }}>
                    Voice notes for prescription
                  </Typography>
                </>
              )}
            </Box>

            {/* Recordings List */}
            {recordings.length > 0 && (
              <Fade in={recordings.length > 0}>
                <Box sx={{ mt: 1, mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, color: "#334155", mb: 1.5 }}
                  >
                    Recordings ({recordings.length})
                  </Typography>
                  <Box
                    sx={{
                      maxHeight: "220px",
                      overflowY: "auto",
                      display: "flex",
                      flexDirection: "column",
                      gap: 1.5,
                    }}
                  >
                    {recordings.map((rec, index) => (
                      <Grow
                        in={true}
                        key={index}
                        timeout={300 + index * 100}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            p: 1.5,
                            border: "1px solid #e2e8f0",
                            borderRadius: 2,
                            bgcolor: "#fff",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <AudioFileIcon
                              sx={{
                                color: "#3b82f6",
                                mr: 1.5,
                                fontSize: "1.5rem",
                              }}
                            />
                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 500,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  fontSize: "0.85rem",
                                  color: "#334155",
                                }}
                              >
                                {rec.name}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ color: "#94a3b8" }}
                              >
                                {formatDuration(rec.duration)}
                              </Typography>
                            </Box>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                removeRecording(index);
                              }}
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                          <audio
                            controls
                            src={rec.url}
                            style={{
                              width: "100%",
                              marginTop: "8px",
                              height: "36px",
                            }}
                          />
                        </Box>
                      </Grow>
                    ))}
                  </Box>
                </Box>
              </Fade>
            )}

            {/* Upload Button */}
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleUpload}
              sx={{
                mt: recordings.length > 0 ? 1 : 4,
                py: 2,
                borderRadius: 2,
                bgcolor:
                  recordings.length > 0 ? "#1e293b" : "#e2e8f0",
                color:
                  recordings.length > 0 ? "#fff" : "#94a3b8",
                "&:hover": {
                  bgcolor:
                    recordings.length > 0 ? "#0f172a" : "#e2e8f0",
                },
                "&.Mui-disabled": {
                  bgcolor: "#e2e8f0",
                  color: "#94a3b8",
                },
                fontSize: "1rem",
                fontWeight: 700,
                textTransform: "none",
                boxShadow: "none",
              }}
              disabled={recordings.length === 0 || isUploading || isRecording}
              startIcon={
                isUploading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  <CloudUploadIcon />
                )
              }
            >
              {isUploading ? "Uploading..." : "Upload Recordings"}
            </Button>

            <Typography
              variant="caption"
              align="center"
              display="block"
              sx={{
                mt: 3,
                color: "#94a3b8",
                fontStyle: "italic",
                fontSize: "0.75rem",
              }}
            >
              Secure upload powered by Hlthtek™ EMR
            </Typography>
          </Card>
        </Grow>
      </Container>
    </Box>
  );
};

export default VoiceUploadByPatient;
